import { useLingui } from '@lingui/react/macro';
import { cn, Label } from '@utima/ui';
import { useFieldState } from 'informed';
import { memo } from 'react';

import {
  MIN_LENGTH,
  SPECIAL_CHARS,
  passwordValidate,
  PasswordValidationError,
} from './passwordValidate';
import { ValidationIcon } from './ValidationIcon';

type PasswordHintProps = {
  className?: string;
};

export const PasswordHint = memo(function ({ className }: PasswordHintProps) {
  const { t } = useLingui();
  const { value: password } = useFieldState('password');
  const { value: confirmPassword } = useFieldState('confirmPassword');

  const validationErrors = passwordValidate(
    password as string,
    confirmPassword as string,
  );

  const hintClassName =
    'inline-flex items-center gap-1 text-xs leading-tight text-muted-fg';

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 border-t border-border pt-3',
        className,
      )}
    >
      <p className='text-xs font-medium text-muted-fg'>
        {t`Password must contain:`}
      </p>
      <div className='grid grid-cols-1 gap-1 sm:grid-cols-2'>
        <Label className={hintClassName}>
          <ValidationIcon
            className='size-3'
            validationErrors={validationErrors}
            targetValidationError={PasswordValidationError.LENGTH}
          />
          {t`Password must be at least ${MIN_LENGTH} characters long`}
        </Label>
        <Label className={hintClassName}>
          <ValidationIcon
            className='size-3'
            validationErrors={validationErrors}
            targetValidationError={PasswordValidationError.UPPERCASE}
          />
          {t`Password must contain at least one uppercase letter`}
        </Label>
        <Label className={hintClassName}>
          <ValidationIcon
            className='size-3'
            validationErrors={validationErrors}
            targetValidationError={PasswordValidationError.NUMBER}
          />
          {t`Password must contain at least one number`}
        </Label>
        <Label className={hintClassName}>
          <ValidationIcon
            className='size-3'
            validationErrors={validationErrors}
            targetValidationError={PasswordValidationError.SPECIAL}
          />
          {t`Password must contain at least one special character (${SPECIAL_CHARS.split('').slice(0, 4).join(',')})`}
        </Label>
      </div>
    </div>
  );
});
